import { useMemo } from 'react';
import {
  getTotalLtcCoveragePayout,
  getTotalIndemnitySurplus,
  getRemainingDeathBenefit,
  getTotalNonLtcPayout,
  getTotalPayout,
  getTotalIneligibleCost,
  getTotalProjectedCareCost,
} from '@shared/calculations/funding/projection/projectionTotals';
import { CollapsibleSection } from './CollapsibleSection';
import { TooltipLabel } from './TooltipLabel';
import { currency } from './projectionTable/formatters';

interface ReturnsSummaryCardProps {
  projection: ClaimProjection;
}

interface Row {
  // Stable render key — labels repeat across sub-row pairs ("Client" appears
  // under several totals), so keys can't come from the label alone.
  key: string;
  label: string;
  tooltip?: string;
  value: number;
  emphasis?: 'total' | 'sub';
}

const PER_SPOUSE_TOOLTIP =
  "This spouse's share of the household total — the same sum filtered to the months tagged to this claimant. Mirrors the funding table's per-spouse rows.";

// Pure row derivation, exported for tests. For joint projections (months
// carry a claimant tag) the cost-coverage totals gain client/partner sub-rows
// so the card reconciles against the funding table's per-spouse presentation
// instead of reading as a ~2× discrepancy (P2-46). The non-LTC components
// (indemnity surplus reserve, remaining death benefit) are policy-level
// balances shared across both spouses, so they don't split.
export function buildReturnsSummaryRows(projection: ClaimProjection): {
  isJoint: boolean;
  ltcRows: Row[];
  nonLtcRows: Row[];
  summaryRows: Row[];
} {
  const { months } = projection;
  const isJoint = months.some(m => m.context.claimant != null);

  const perSpouseRows = (keyPrefix: string, totalOf: (months: MonthlyEntry[]) => number): Row[] => {
    if (!isJoint) return [];
    return (['client', 'partner'] as const).map(claimant => ({
      key: `${keyPrefix}-${claimant}`,
      label: claimant === 'client' ? 'Client' : 'Partner',
      tooltip: PER_SPOUSE_TOOLTIP,
      value: totalOf(months.filter(m => m.context.claimant === claimant)),
      emphasis: 'sub' as const,
    }));
  };

  const ltcRows: Row[] = [
    {
      key: 'ltc-payout',
      label: 'LTC payout',
      tooltip:
        'Dollars the policy applied to care cost — direct coverage plus any banked indemnity surplus later drawn to cover a shortfall, and the portion of any retroactive elimination-period lump sum applied to care cost (excess banks as surplus and counts when later drawn).',
      value: getTotalLtcCoveragePayout(months),
      emphasis: 'total',
    },
    ...perSpouseRows('ltc-payout', getTotalLtcCoveragePayout),
  ];

  const nonLtcRows: Row[] = [
    {
      key: 'indemnity-surplus',
      label: 'Indemnity surplus',
      tooltip:
        'Indemnity cash remaining at the end of the projection (the carried balance, not gross accrual). Surplus later drawn to cover care cost is counted under LTC payout; only the never-used remainder is non-LTC.',
      value: getTotalIndemnitySurplus(months),
      emphasis: 'sub',
    },
    {
      key: 'remaining-death-benefit',
      label: 'Remaining death benefit',
      tooltip:
        'Death benefit pool remaining at the end of the projection horizon. Paid to beneficiaries; counts as a non-LTC return.',
      value: getRemainingDeathBenefit(months),
      emphasis: 'sub',
    },
    {
      key: 'non-ltc-payout',
      label: 'Non-LTC payout',
      tooltip: 'Indemnity surplus + remaining death benefit.',
      value: getTotalNonLtcPayout(months),
      emphasis: 'total',
    },
  ];

  const summaryRows: Row[] = [
    {
      key: 'projected-cost',
      label: 'Total projected LTC cost',
      tooltip:
        'Inflation-adjusted care cost across the entire projection, regardless of who pays. Totaled across every projected month.',
      value: getTotalProjectedCareCost(months),
    },
    ...perSpouseRows('projected-cost', getTotalProjectedCareCost),
    {
      key: 'total-payout',
      label: 'Total payout',
      tooltip: 'LTC payout + Non-LTC payout.',
      value: getTotalPayout(months),
      emphasis: 'total',
    },
    {
      key: 'ineligible-cost',
      label: 'Ineligible cost (out-of-pocket)',
      tooltip:
        'Net care cost not covered by the policy across the projection. Includes months in waiting period or after pool exhaustion.',
      value: getTotalIneligibleCost(months),
    },
    ...perSpouseRows('ineligible-cost', getTotalIneligibleCost),
  ];

  return { isJoint, ltcRows, nonLtcRows, summaryRows };
}

/**
 * High-level payout totals derived from the new strategy-based projection
 * engine. Mirrors the LTC / Non-LTC split used by the legacy FundingTable so
 * the two views can be compared side-by-side during parity validation. Joint
 * projections sum BOTH spouses' months, so the card is labeled "household
 * totals" and the splittable totals carry per-spouse sub-rows (P2-46).
 *
 * ROI / CAGR / IRR are intentionally omitted in step 1 — they require premium
 * cashflow inputs that the projection engine does not yet model.
 */
export function ReturnsSummaryCard({ projection }: ReturnsSummaryCardProps) {
  // The subtitle stats reuse the values already computed for the summary rows
  // rather than re-walking the months on every render.
  const { isJoint, ltcRows, nonLtcRows, summaryRows, totalPayout, ineligibleCost } = useMemo(() => {
    const rows = buildReturnsSummaryRows(projection);
    return {
      ...rows,
      totalPayout: rows.summaryRows.find(r => r.key === 'total-payout')?.value ?? 0,
      ineligibleCost: rows.summaryRows.find(r => r.key === 'ineligible-cost')?.value ?? 0,
    };
  }, [projection]);

  const subtitle = (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Stat
        label="Total payout"
        value={totalPayout}
      />
      <span className="text-gray-300">·</span>
      <Stat
        label="Ineligible cost"
        value={ineligibleCost}
      />
    </span>
  );

  return (
    <CollapsibleSection
      title={isJoint ? 'Policy Payout Summary — Combined Totals' : 'Policy Payout Summary'}
      subtitle={subtitle}
      defaultOpen={false}
    >
      <p className="mb-4 text-xs text-gray-500">
        {isJoint
          ? 'Both spouses combined; Client / Partner sub-rows match the funding table split.'
          : 'Aggregated payouts across the full projection.'}
      </p>
      <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
        <RowGroup
          heading="LTC"
          rows={ltcRows}
        />
        <RowGroup
          heading="Non-LTC"
          rows={nonLtcRows}
        />
        <RowGroup
          heading="Summary"
          rows={summaryRows}
          className="md:col-span-2"
        />
      </div>
      <p className="mt-4 text-[11px] text-gray-500">
        ROI, CAGR, and IRR are not yet shown here.
      </p>
    </CollapsibleSection>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 text-sm text-gray-500">
      <span>{label}</span>
      <span className="font-semibold tabular-nums">{currency(value)}</span>
    </span>
  );
}

function RowGroup({
  heading,
  rows,
  className,
}: {
  heading: string;
  rows: Row[];
  className?: string;
}) {
  return (
    <section className={className}>
      <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
        {heading}
      </h3>
      <dl className="divide-y divide-gray-100">
        {rows.map(row => (
          <div
            key={row.key}
            className="flex items-baseline justify-between gap-4 py-1.5 text-sm"
          >
            <dt
              className={
                row.emphasis === 'total'
                  ? 'font-medium text-gray-900'
                  : row.emphasis === 'sub'
                    ? 'pl-3 text-gray-600'
                    : 'text-gray-700'
              }
            >
              {row.tooltip ? (
                <TooltipLabel
                  label={row.label}
                  tooltip={row.tooltip}
                />
              ) : (
                row.label
              )}
            </dt>
            <dd
              className={`tabular-nums ${
                row.emphasis === 'total' ? 'font-semibold text-gray-900' : 'text-gray-800'
              }`}
            >
              {currency(row.value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
